import React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { Icon, Popover, Position } from '@blueprintjs/core';
import { UNITS } from 'helpers/units';

// A material's unit: a small button with the short name ("Sq. Ft", "Each")
// that opens a menu like the price book picker's (styles .ps-menu): a search,
// then the units in their groups, each with its full name. Values are the
// same as before (helpers/units).

const GROUP_NAMES = { area: 'Area', length: 'Length', volume: 'Volume', weight: 'Weight', count: 'Count', specialty: 'Other' };

// "Sq. Ft (Square Feet )" -> short "Sq. Ft", full "Square Feet".
const parts = (label) => {
  const match = /^(.*?)\s*\((.*)\)\s*$/.exec(label);
  return match ? { short: match[1].trim(), full: match[2].trim() } : { short: label, full: null };
};

export const unitLabel = (value) => {
  for (const key of Object.keys(UNITS)) {
    const found = UNITS[key].options.find((option) => option.value === value);
    if (found) return parts(found.label).short;
  }
  return value || null;
};

@observer
class UnitPicker extends React.Component {
  @observable open = false;
  @observable query = '';
  @observable active = 0;
  searchRef = React.createRef();

  // Focus the search once the menu is in place. (autoFocus would focus it
  // before the menu is positioned, while it's still at the top of the page,
  // and the browser scrolls up to it.)
  focusSearch = () => {
    const input = this.searchRef.current;
    if (input) input.focus({ preventScroll: true });
  };

  get groups() {
    const typed = this.query.trim().toLowerCase();
    return Object.keys(UNITS).map((key) => ({
      key,
      name: GROUP_NAMES[key] || UNITS[key].label,
      options: UNITS[key].options.filter((option) => !typed || option.label.toLowerCase().includes(typed) || (GROUP_NAMES[key] || '').toLowerCase().includes(typed)),
    })).filter((group) => group.options.length > 0);
  }

  get flat() {
    return this.groups.flatMap((group) => group.options);
  }

  pick(value) {
    this.props.onChange(value);
    this.open = false;
    this.query = '';
  }

  onKeyDown = (event) => {
    const options = this.flat;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.active = Math.min(this.active + 1, options.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.active = Math.max(this.active - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (options[this.active]) this.pick(options[this.active].value);
    } else if (event.key === 'Escape') {
      this.open = false;
    }
  };

  renderOption(option) {
    const { value } = this.props;
    const { short, full } = parts(option.label);
    const chosen = (value || '') === option.value;
    const index = this.flat.indexOf(option);
    return (
      <li key={option.value}>
        <button
          type="button"
          role="option"
          aria-selected={chosen}
          className={`unit-opt${chosen ? ' is-chosen' : ''}${index === this.active ? ' is-active' : ''}`}
          onMouseEnter={() => { this.active = index; }}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => this.pick(option.value)}
        >
          <span className="unit-opt__short">{short}</span>
          {full && <span className="unit-opt__full">{full}</span>}
        </button>
      </li>
    );
  }

  // Every unit at once: one column per group, a search that narrows them,
  // and "No unit" beside it.
  renderMenu() {
    const typed = this.query.trim().toLowerCase();
    const { value } = this.props;
    return (
      <div className="ps-menu unit-grid-menu">
        <div className="unit-grid__top">
          <div className="unit-search">
            <Icon icon="search" iconSize={12} />
            <input
              ref={this.searchRef}
              aria-label="Search units"
              placeholder="Search units"
              value={this.query}
              onChange={(event) => { this.query = event.target.value; this.active = 0; }}
              onKeyDown={this.onKeyDown}
            />
          </div>
          <button
            type="button"
            className={`unit-none${!value ? ' is-chosen' : ''}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => this.pick('')}
          >
            {!value && <Icon icon="tick" iconSize={12} />} No unit
          </button>
        </div>
        <div className="unit-grid" role="listbox" aria-label="Units">
          {Object.keys(UNITS).map((key) => {
            const options = UNITS[key].options.filter((option) => !typed || option.label.toLowerCase().includes(typed) || (GROUP_NAMES[key] || '').toLowerCase().includes(typed));
            return (
              <section key={key} className="unit-col" aria-label={GROUP_NAMES[key]}>
                <h4 className="unit-col__title">{GROUP_NAMES[key] || UNITS[key].label}</h4>
                {options.length > 0
                  ? <ul>{options.map((option) => this.renderOption(option))}</ul>
                  : <p className="unit-col__none">No match</p>}
              </section>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    const { value } = this.props;
    const label = unitLabel(value);
    return (
      <Popover
        isOpen={this.open}
        onInteraction={(next) => { this.open = next; if (!next) this.query = ''; }}
        position={Position.BOTTOM}
        minimal
        popoverClassName="ps-menu-popover"
        autoFocus={false}
        enforceFocus={false}
        onOpened={this.focusSearch}
        content={this.renderMenu()}
      >
        <button type="button" className={`de-unit${label ? '' : ' is-empty'}`} aria-label={`Unit: ${label || 'none'}`} aria-haspopup="listbox">
          <span>{label || 'None'}</span>
          <Icon icon="chevron-down" iconSize={12} />
        </button>
      </Popover>
    );
  }
}

export default UnitPicker;
